import React, { useMemo } from 'react';
import { Table, Tag } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import type { AttendanceRecord } from '../../../services/pw9411Api';

interface AttendanceTableProps {
  records: AttendanceRecord[];
  total: number;
}

type RowWithKey = AttendanceRecord & { _key: string };

/** Render status_code as a coloured tag */
const StatusTag: React.FC<{ code: string }> = ({ code }) => {
  const trimmed = (code || '').trim();
  if (!trimmed) return <Tag color="default">正常</Tag>;
  switch (trimmed) {
    case 'L': return <Tag color="orange">請假</Tag>;
    case 'T': return <Tag color="red">遲到</Tag>;
    case 'S': return <Tag color="purple">早退</Tag>;
    case 'X': return <Tag color="volcano">曠職</Tag>;
    case 'Y': return <Tag color="geekblue">出差</Tag>;
    case 'N': return <Tag color="green">正常</Tag>;
    default:  return <Tag>{trimmed}</Tag>;
  }
};

const columns: ColumnsType<RowWithKey> = [
  {
    title: '出勤日期',
    dataIndex: 'attend_date',
    key: 'attend_date',
    width: 110,
    fixed: 'left' as const,
    render: (v: string) => v || '—',
  },
  {
    title: '出勤狀態',
    dataIndex: 'status_code',
    key: 'status_code',
    width: 90,
    render: (v: string) => <StatusTag code={v} />,
  },
  {
    title: '上班時間',
    dataIndex: 'clock_in',
    key: 'clock_in',
    width: 90,
    render: (v: string) => (v || '').trim() || '—',
  },
  {
    title: '下班時間',
    dataIndex: 'clock_out',
    key: 'clock_out',
    width: 90,
    render: (v: string) => (v || '').trim() || '—',
  },
  {
    title: '加班開始',
    dataIndex: 'ot_start',
    key: 'ot_start',
    width: 90,
    render: (v: string) => (v || '').trim() || '—',
  },
  {
    title: '加班結束',
    dataIndex: 'ot_end',
    key: 'ot_end',
    width: 90,
    render: (v: string) => (v || '').trim() || '—',
  },
  {
    title: '遲到(分)',
    dataIndex: 'late_minutes',
    key: 'late_minutes',
    width: 85,
    align: 'right' as const,
    render: (v: number) =>
      v > 0 ? <span style={{ color: '#f5222d' }}>{v}</span> : '0',
  },
  {
    title: '早退(分)',
    dataIndex: 'early_minutes',
    key: 'early_minutes',
    width: 85,
    align: 'right' as const,
    render: (v: number) =>
      v > 0 ? <span style={{ color: '#fa8c16' }}>{v}</span> : '0',
  },
  {
    title: '請假單號',
    dataIndex: 'leave_no',
    key: 'leave_no',
    width: 120,
    render: (v: string) => (v || '').trim() || '—',
  },
  {
    title: '出差單號',
    dataIndex: 'trip_no',
    key: 'trip_no',
    width: 120,
    render: (v: string) => (v || '').trim() || '—',
  },
  {
    title: '備註說明',
    dataIndex: 'shift_note',
    key: 'shift_note',
    ellipsis: true,
    render: (v: string) => (v || '').trim() || '',
  },
];

const AttendanceTable: React.FC<AttendanceTableProps> = React.memo(({ records, total }) => {
  const dataSource = useMemo(
    () =>
      records.map((r, i) => ({
        ...r,
        _key: `${r.attend_date}-${i}`,
      })),
    [records],
  );

  return (
    <Table<RowWithKey>
      columns={columns}
      dataSource={dataSource}
      rowKey="_key"
      size="small"
      scroll={{ x: 1100, y: 500 }}
      pagination={{
        pageSize: 50,
        showSizeChanger: true,
        pageSizeOptions: ['20', '50', '100'],
        showTotal: (t) => `共 ${t} 筆`,
      }}
      summary={() => (
        <Table.Summary fixed>
          <Table.Summary.Row>
            <Table.Summary.Cell index={0} colSpan={2}>
              <span className="text-sm text-gray-500">合計 {total} 筆出勤記錄</span>
            </Table.Summary.Cell>
          </Table.Summary.Row>
        </Table.Summary>
      )}
    />
  );
});

AttendanceTable.displayName = 'AttendanceTable';

export default AttendanceTable;
